import { request } from '@app/http/request'
import { appConfig } from '@app/shared/config'
import { searchParamsToQuery } from '@app/utils/url'
import type { PaginatedListResponse } from '@backend-models/common/paging/paginated-list-response'
import type { SearchablePaginatedListRequest } from '@backend-models/common/paging/searchable-paginated-list-request'
import type { SpResponse } from '@backend-models/common/request/sp-response'
import type { GetMeResponse } from '@backend-models/users/get-me/get-me-response'
import type { GrantConsentResponse } from '@backend-models/users/grant-consent/grant-consent-response'
import type { StartImpersonationSessionRequest } from '@backend-models/users/impersonation/start-impersonation-session-request'
import type { StopImpersonationSessionResponse } from '@backend-models/users/impersonation/stop-impersonation-session-response'
import type { InviteUserRequest } from '@backend-models/users/invite/invite-user-request'
import type { InviteUserResponse } from '@backend-models/users/invite/invite-user-response'
import type { ListUsersResponse } from '@backend-models/users/list-users/list-users-response'
import type { AuditTrailDto } from '../../backend-models/audit-trail/audit-trail-dto'

export const getUsers = async (params?: SearchablePaginatedListRequest, hideLoading = false): Promise<PaginatedListResponse<ListUsersResponse>> => {
  try {
    const query = searchParamsToQuery({ ...params })
    const req = await request(`${appConfig.api}/users${query}`, { auth: true, hideLoading })
    const res = await req.json()
    return res
  } catch (error) {
    console.error(error)
    return { data: [], success: false, page: 0, pageCount: 0, pageSize: 0, totalItems: 0 }
  }
}

export const getMe = async (hideLoading = true): Promise<SpResponse<GetMeResponse | null>> => {
  try {
    const req = await request(`${appConfig.api}/users/get-me`, { hideLoading, auth: true })
    const res = await req.json()
    return res
  } catch (error) {
    console.error(error)
    return { data: null, success: false }
  }
}

export const grantConsent = async (): Promise<SpResponse<GrantConsentResponse | null>> => {
  try {
    const req = await request(`${appConfig.api}/users/grant-consent`, { method: 'POST', auth: true })
    const res = await req.json()
    return res
  } catch (error) {
    console.error(error)
    return { data: null, success: false }
  }
}

export const startImpersonation = async (data: StartImpersonationSessionRequest): Promise<SpResponse<{ token: string } | null>> => {
  try {
    const req = await request(`${appConfig.api}/users/impersonation/start`, { method: 'POST', auth: true, body: JSON.stringify(data), json: true })
    const res = await req.json()
    if (res.success && res.data?.token) {
      sessionStorage.setItem('impersonation-token', res.data.token)
    }
    return res
  } catch (error) {
    console.error(error)
    return { data: null, success: false }
  }
}

export const stopImpersonation = async (): Promise<SpResponse<StopImpersonationSessionResponse | null>> => {
  try {
    const req = await request(`${appConfig.api}/users/impersonation/stop`, { method: 'POST', auth: true, body: JSON.stringify({}), json: true })
    const res = await req.json()
    // Always clear token, even if API call technically failed
    sessionStorage.removeItem('impersonation-token')
    return res
  } catch (error) {
    console.error(error)
    sessionStorage.removeItem('impersonation-token')
    return { data: null, success: false }
  }
}

export const stopAllImpersonations = async (): Promise<SpResponse<StopImpersonationSessionResponse | null>> => {
  try {
    const req = await request(`${appConfig.api}/users/impersonation/stop`, {
      method: 'POST',
      auth: true,
      body: JSON.stringify({ stopAll: true }),
      json: true,
    })
    const res = await req.json()
    return res
  } catch (error) {
    console.error(error)
    return { data: null, success: false }
  }
}

export const inviteUser = async (data: InviteUserRequest): Promise<SpResponse<InviteUserResponse | null>> => {
  try {
    const req = await request(`${appConfig.api}/users/invite`, { method: 'POST', auth: true, body: JSON.stringify(data), json: true })
    const res = await req.json()
    return res
  } catch (error) {
    console.error(error)
    return { data: null, success: false }
  }
}

export const getAuditTrail = async (
  params?: SearchablePaginatedListRequest,
  hideLoading = false,
  path = 'users/impersonation'
): Promise<PaginatedListResponse<AuditTrailDto>> => {
  try {
    const query = searchParamsToQuery({ ...params })
    const req = await request(`${appConfig.api}/${path}/audit-trail${query}`, { auth: true, hideLoading })
    const res = await req.json()
    return res
  } catch (error) {
    console.error(error)
    return { data: [], success: false, page: 0, pageCount: 0, pageSize: 0, totalItems: 0 }
  }
}
